import type { AxiosInstance } from "axios";

/**
 * The Store Health board, as the client app sees it.
 *
 * These types MIRROR `packages/health` rather than importing it. apps/web cannot depend
 * on that package: it reaches ClickHouse and Postgres, and pulling it into a browser
 * bundle would drag a database driver in with it. The shapes are checked against the
 * real endpoint by `scripts/check-health-contract.mjs` instead, which is the same trade
 * `lib/reports.ts` makes for the report contracts.
 */

export type HealthStatus = "green" | "amber" | "red";

export interface HealthFinding {
  readonly severity: "red" | "amber";
  /** Stable key — an alert rule references this, so the UI must not match on prose. */
  readonly code: string;
  readonly message: string;
  /** The incident the rule exists because of. Shown, because "why" earns trust. */
  readonly because: string;
}

export interface StoreSignals {
  readonly lastEventAt: string | null;
  readonly events48h: number;
  readonly sessions48h: number;
  readonly trackerVersion: string;
  readonly eventsV1: number;
  readonly eventsV2: number;
  readonly lastOrderAt: string | null;
  readonly orders48h: number;
  readonly ordersTracked48h: number;
  readonly ordersSettled48h: number;
  readonly ordersUnattributed48h: number;
}

export interface AdSyncHealthEntry {
  readonly platform: string;
  readonly connected: boolean;
  readonly status: HealthStatus;
  /** Newest success across all tiers. */
  readonly lastSuccessAt: string | null;
  /** Per-tier last-success, so a merchant sees hot vs cold cadence. */
  readonly lastHotAt: string | null;
  readonly lastWarmAt: string | null;
  readonly lastColdAt: string | null;
  readonly lastManualAt: string | null;
  readonly circuitOpen: boolean;
  readonly consecutiveFailures: number;
}

/** A platform pipeline (attribution, ingestion) with its own last-run. */
export interface HealthPipeline {
  readonly key: string;
  readonly label: string;
  readonly status: HealthStatus;
  readonly lastSuccessAt: string | null;
  /** Why it is amber/red — null when green. */
  readonly reason: string | null;
}

export interface StoreHealthRow {
  readonly store: {
    readonly id: string;
    readonly shopDomain: string;
    readonly displayName: string;
    readonly status: string;
    readonly onboarding: string;
  };
  readonly signals: StoreSignals;
  readonly verdict: { readonly status: HealthStatus; readonly findings: readonly HealthFinding[] };
  readonly v2Share: number | null;
  readonly adSyncs: readonly AdSyncHealthEntry[];
}

export interface HealthBoard {
  readonly generatedAt: string;
  readonly stores: readonly StoreHealthRow[];
  readonly pipelines: readonly HealthPipeline[];
  readonly dataFreshness: { readonly status: HealthStatus; readonly minutesBehind: number | null };
}

export const HEALTH_PATH = "/api/v2/health/stores";

export async function fetchHealth(client: AxiosInstance): Promise<HealthBoard> {
  const response = await client.get<{ success: boolean; message: string; data: HealthBoard }>(HEALTH_PATH);
  return response.data.data;
}

/** "4 minutes ago" — relative, because a board is read to answer "is it current?". */
export function timeAgo(iso: string | null, now: Date = new Date()): string {
  if (iso === null) return "never";
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "unknown";

  const minutes = Math.floor((now.getTime() - then) / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${String(minutes)} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${String(hours)} h ago`;
  return `${String(Math.floor(hours / 24))} d ago`;
}
